import { apiErrorMessage, apiErrorStatus } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { useActiveDocument } from '@/hooks/useActiveDocument';
import { type RetrySkippedPages, useRetrySkippedPages } from '@/hooks/useRetrySkippedPages';
import type { Category, DocumentModel, FindingStatus, PageModel } from '@/types/documents.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReviewDocumentState {
  document: DocumentModel | null;
  /** The page the user is on, already fetched, or `null` while it loads. */
  page: PageModel | null;
  pageNumber: number;
  setPageNumber: (page: number) => void;
  isLoading: boolean;
  isPageLoading: boolean;
  /** The document is gone from the server (expired or deleted elsewhere). */
  isExpired: boolean;
  error: string | null;
  /** Exclude or restore one finding, then refresh what changed. */
  setFindingStatus: (findingId: string, status: FindingStatus) => Promise<void>;
  /** Exclude or restore a whole category. */
  setCategoryExcluded: (category: Category, excluded: boolean) => Promise<void>;
  /** Re-read the current page and the document summary (after adding a manual finding). */
  refresh: () => Promise<void>;
  /**
   * The re-run of the skipped pages. While it runs the backend refuses page reads and new findings
   * (422) and a reload would show the retry job in place of the finished run, so every mutation
   * here is a no-op and paging is limited to pages already loaded until it ends.
   */
  retry: RetrySkippedPages;
}

/**
 * The review screen's data: the document summary and the current page's findings.
 *
 * Pages are cached in a ref so paging back and forth does not re-request what is already known,
 * and the cache is dropped whenever a mutation changes the findings — the counts in the summary
 * and the statuses on every page can all move at once (excluding a category touches every page).
 */
export const useReviewDocument = (documentId: string): ReviewDocumentState => {
  const { t } = useTranslation();
  const [, setActiveDocumentId] = useActiveDocument();
  const [document, setDocument] = useState<DocumentModel | null>(null);
  const [page, setPage] = useState<PageModel | null>(null);
  const [pageNumber, setPageNumberState] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageCache = useRef(new Map<number, PageModel>());

  /** A 404 means the in-memory session is gone; the stored id is then worthless. */
  const handleFailure = useCallback(
    (err: unknown, fallbackKey: string) => {
      if (apiErrorStatus(err) === 404) {
        setIsExpired(true);
        setActiveDocumentId(null);
        return;
      }
      setError(apiErrorMessage(err, t(fallbackKey)));
    },
    [setActiveDocumentId, t]
  );

  const loadDocument = useCallback(async () => {
    try {
      const loaded = await documentsApi.get(documentId);
      setDocument(loaded);
      return loaded;
    } catch (err) {
      handleFailure(err, 'review.loadFailed');
      return null;
    }
  }, [documentId, handleFailure]);

  const loadPage = useCallback(
    async (number: number, { force = false }: { force?: boolean } = {}) => {
      const cached = pageCache.current.get(number);
      if (cached && !force) {
        setPage(cached);
        return;
      }
      setIsPageLoading(true);
      try {
        const loaded = await documentsApi.page(documentId, number);
        pageCache.current.set(number, loaded);
        setPage(loaded);
      } catch (err) {
        handleFailure(err, 'review.pageFailed');
      } finally {
        setIsPageLoading(false);
      }
    },
    [documentId, handleFailure]
  );

  // First load: the summary decides how many pages there are, so the page follows it.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    loadDocument().then((loaded) => {
      if (cancelled || !loaded) {
        setIsLoading(false);
        return;
      }
      setActiveDocumentId(loaded.id);
      loadPage(1).finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [loadDocument, loadPage, setActiveDocumentId]);

  /** Drop the cache and re-read both the summary and the visible page. */
  const refresh = useCallback(async () => {
    pageCache.current.clear();
    await Promise.all([loadDocument(), loadPage(pageNumber, { force: true })]);
  }, [loadDocument, loadPage, pageNumber]);

  const retry = useRetrySkippedPages(documentId, { lastFinishedAt: document?.detection?.finished_at, onDone: refresh });

  const setPageNumber = useCallback(
    (next: number) => {
      if (!document) return;
      const clamped = Math.min(Math.max(next, 1), document.page_count);
      if (retry.isRetrying && !pageCache.current.has(clamped)) return;
      setPageNumberState(clamped);
      loadPage(clamped);
    },
    [document, loadPage, retry.isRetrying]
  );

  const setFindingStatus = useCallback(
    async (findingId: string, status: FindingStatus) => {
      if (retry.isRetrying) return;
      setError(null);
      try {
        await documentsApi.updateFinding(documentId, findingId, { status });
        await refresh();
      } catch (err) {
        handleFailure(err, 'review.finding.updateFailed');
      }
    },
    [documentId, handleFailure, refresh, retry.isRetrying]
  );

  const setCategoryExcluded = useCallback(
    async (category: Category, excluded: boolean) => {
      if (retry.isRetrying) return;
      setError(null);
      try {
        await documentsApi.updateCategory(documentId, category, { excluded });
        await refresh();
      } catch (err) {
        handleFailure(err, 'review.categories.updateFailed');
      }
    },
    [documentId, handleFailure, refresh, retry.isRetrying]
  );

  return {
    document,
    page,
    pageNumber,
    setPageNumber,
    isLoading,
    isPageLoading,
    isExpired,
    error,
    setFindingStatus,
    setCategoryExcluded,
    refresh,
    retry,
  };
};
